import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  History,
  Clock,
  User,
  RotateCcw,
  Eye,
  ChevronRight,
  GitBranch,
  Loader2,
  Calendar,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/use-toast'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import {
  workflowService,
  type WorkflowDetail,
  type WorkflowVersion,
} from '@/lib/api/services/workflows'

export default function WorkflowHistory() {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null)
  const [versions, setVersions] = useState<WorkflowVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState<number | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<WorkflowVersion | null>(null)

  const loadData = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      const [workflowData, versionsData] = await Promise.all([
        workflowService.getWorkflow(id),
        workflowService.getWorkflowVersions(id),
      ])
      setWorkflow(workflowData)
      setVersions(versionsData)
    } catch (error) {
      console.error('Failed to load workflow history:', error)
      toast({
        title: 'Error',
        description: 'Failed to load workflow history',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRestore = async (versionNumber: number) => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Restore Version',
      description: `Are you sure you want to restore to version ${versionNumber}? This will overwrite the current workflow.`,
      confirmLabel: 'Restore',
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      setRestoring(versionNumber)
      await workflowService.restoreWorkflowVersion(id, versionNumber)
      toast({
        title: 'Success',
        description: `Workflow restored to version ${versionNumber}`,
      })
      loadData()
    } catch (error) {
      console.error('Failed to restore version:', error)
      toast({
        title: 'Error',
        description: 'Failed to restore workflow version',
        variant: 'destructive',
      })
    } finally {
      setRestoring(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(dateString)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!workflow) {
    return (
      <div className="text-center py-12">
        <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Workflow Not Found
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          The workflow you&apos;re looking for doesn&apos;t exist or has been deleted.
        </p>
        <Link
          to="/workflows"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Workflows
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {ConfirmDialogComponent}
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-3">
        <Link
          to="/workflows"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/workflows" className="hover:text-gray-700 dark:hover:text-gray-300">
            Workflows
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link
            to={`/workflows/builder?id=${id}`}
            className="hover:text-gray-700 dark:hover:text-gray-300"
          >
            {workflow.name}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span>History</span>
        </div>
      </div>

      <PageHeader
        title="Version History"
        description={`View and restore previous versions of "${workflow.name}"`}
        actions={
          <Link
            to={`/workflows/builder?id=${id}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <GitBranch className="h-4 w-4" />
            Edit Workflow
          </Link>
        }
      />

      {/* Current Version Info */}
      <FluentCard className="p-5">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600">
            <GitBranch className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {workflow.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {workflow.description || 'No description'}
            </p>
          </div>
          <Badge
            variant={
              workflow.status === 'active'
                ? 'success'
                : workflow.status === 'draft'
                ? 'warning'
                : 'secondary'
            }
          >
            {workflow.status}
          </Badge>
        </div>
      </FluentCard>

      {/* Version History */}
      <FluentCard className="overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <History className="h-5 w-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Version History
            </h3>
            <Badge variant="secondary">{versions.length} versions</Badge>
          </div>
        </div>

        {versions.length === 0 ? (
          <div className="py-12 text-center">
            <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Version History
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              This workflow doesn&apos;t have any saved versions yet. Versions are created when you save changes to the workflow.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {versions.map((version, index) => {
              const isCurrentVersion = index === 0
              return (
                <div
                  key={version.id}
                  className={`px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                    selectedVersion?.id === version.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Version Indicator */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                          isCurrentVersion
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        v{version.versionNumber}
                      </div>
                      {index < versions.length - 1 && (
                        <div className="w-0.5 h-8 bg-gray-200 dark:bg-gray-700 my-1" />
                      )}
                    </div>

                    {/* Version Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                          Version {version.versionNumber}
                        </span>
                        {isCurrentVersion && (
                          <Badge variant="success" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>

                      {version.changeDescription && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {version.changeDescription}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {getRelativeTime(version.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(version.createdAt)}
                        </span>
                        {version.createdByUserId && (
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            User
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setSelectedVersion(
                            selectedVersion?.id === version.id ? null : version
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </button>
                      {!isCurrentVersion && (
                        <button
                          onClick={() => handleRestore(version.versionNumber)}
                          disabled={restoring !== null}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {restoring === version.versionNumber ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                          Restore
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Version Preview */}
                  {selectedVersion?.id === version.id && version.definition && (
                    <div className="mt-4 ml-14 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Version Definition Preview
                      </h4>
                      <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto max-h-48 overflow-y-auto">
                        {JSON.stringify(JSON.parse(version.definition), null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </FluentCard>
    </div>
  )
}
